import Breadcrumb, { BreadcrumbLink } from "components/common/Breadcrumb";
import CustomerSelect from "components/common/CustomerSelect";
import "components/common/DatePicker.css";
import JobSelect from "components/common/JobSelect";
import ContentLoading from "components/common/ContentLoading";
import ProductSelect from "components/common/ProductSelect";
import UserIdAndName from "interfaces/UserIdAndName";
import * as moment from "moment";
import * as React from "react";
import DatePicker from "react-datepicker";
import { RouteComponentProps } from "react-router";
import { Option } from "react-select";
import apifetch from "utils/apifetch";
import downloadResponse from "utils/downloadResponse";

interface State {
    isLoading: boolean;
    isSubmitting: boolean;
    submitType?: string;
    fromDate?: moment.Moment;
    toDate?: moment.Moment;
    quoteStatusId: number;
    salespersonUserId?: number;
    customerId?: number;
    productId?: number;
    city?: string;
    jobId?: number;
    includeArchived: boolean;
    salespeople: UserIdAndName[];
}

export default class Quotes extends React.Component<
    RouteComponentProps<{}>,
    State
> {
    constructor(props: RouteComponentProps<{}>) {
        super(props);

        this.state = {
            isLoading: true,
            isSubmitting: false,
            quoteStatusId: 0,
            includeArchived: false,
            salespeople: [],
        };

        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.handleBooleanInputChanged = this.handleBooleanInputChanged.bind(this);
        this.handleFromDateChanged = this.handleFromDateChanged.bind(this);
        this.handleToDateChanged = this.handleToDateChanged.bind(this);
        this.handleCustomerSelected = this.handleCustomerSelected.bind(this);
        this.handleJobSelected = this.handleJobSelected.bind(this);
        this.handleProductSelected = this.handleProductSelected.bind(this);
        this.handleViewExcel = this.handleViewExcel.bind(this);
    }

    public async componentDidMount() {
        if (this.state.isLoading) {
            let response = await apifetch("quotes/add", undefined, true);
            if (response.ok) {
                const data = (await response.json()) as {
                    salespeople: UserIdAndName[];
                };

                this.setState({
                    isLoading: false,
                    salespeople: data.salespeople,
                });

                document.title = "Quote Info - Reports - " + SITE_NAME;
            }
        }
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="container">
                    <ContentLoading />
                </div>
            );
        }

        return (
            <div className="container">
                <div className="animated fadeIn">
                    <Breadcrumb>
                        <BreadcrumbLink to="/">Home</BreadcrumbLink>
                        <BreadcrumbLink to="/reports">Reports</BreadcrumbLink>
                        <BreadcrumbLink isActive={true}>Quotes</BreadcrumbLink>
                    </Breadcrumb>
                    <div className="box-wrap">
                        <div className="card-block">
                            <h3 className="block-title">Quotes Report</h3>
                            <div className="form-wrapper">
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">From</span>
                                        <DatePicker
                                            name="fromDate"
                                            selected={this.state.fromDate}
                                            onChange={this.handleFromDateChanged}
                                            dateFormat="LL"
                                            peekNextMonth
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            className="form-control"
                                            popperPlacement="bottom"
                                            popperModifiers={{
                                                flip: {
                                                    behavior: ["bottom"],
                                                },
                                                preventOverflow: {
                                                    enabled: false,
                                                },
                                                hide: {
                                                    enabled: false,
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">To</span>
                                        <DatePicker
                                            name="toDate"
                                            selected={this.state.toDate}
                                            onChange={this.handleToDateChanged}
                                            dateFormat="LL"
                                            peekNextMonth
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            className="form-control"
                                            popperPlacement="bottom"
                                            popperModifiers={{
                                                flip: {
                                                    behavior: ["bottom"],
                                                },
                                                preventOverflow: {
                                                    enabled: false,
                                                },
                                                hide: {
                                                    enabled: false,
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Quote Status</span>
                                        <select
                                            name="quoteStatusId"
                                            className="form-control selectbox"
                                            style={{ paddingTop: "0", paddingBottom: "0" }}
                                            onChange={this.handleInputChanged}
                                            value={this.state.quoteStatusId}
                                        >
                                            <option value={0}>All quotes</option>
                                            <option value={1}>Open quotes</option>
                                            <option value={2}>Closed quotes</option>
                                        </select>
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Salesperson</span>
                                        <select
                                            name="salespersonUserId"
                                            className="form-control selectbox"
                                            style={{ paddingTop: "0", paddingBottom: "0" }}
                                            onChange={this.handleInputChanged}
                                            value={this.state.salespersonUserId}
                                        >
                                            <option value="">All salespeople</option>
                                            {this.state.salespeople.map((value, index) => (
                                                <option value={value.id}>{value.fullName}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Customer</span>
                                        <CustomerSelect
                                            onItemSelect={this.handleCustomerSelected}
                                        />
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Project Name</span>
                                        <JobSelect onItemSelect={this.handleJobSelected} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">City</span>
                                        <input
                                            type="text"
                                            name="city"
                                            className="form-control"
                                            onChange={this.handleInputChanged}
                                            value={this.state.city}
                                        />
                                    </div>
                                    <div className="field-group col-sm-6">
                                        <span className="field-label">Item</span>
                                        <ProductSelect onItemSelect={this.handleProductSelected} />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="field-group col-sm-6 mt-2 mb-2">
                                        <span className="field-label mt-0">Include Archived</span>
                                        <p className="custom-checkbox-wrap">
                                            <input
                                                className="custom-checkbox"
                                                id="include-archived-yes"
                                                name="includeArchived"
                                                type="radio"
                                                value="true"
                                                onChange={this.handleBooleanInputChanged}
                                                checked={this.state.includeArchived}
                                            />
                                            <label htmlFor="include-archived-yes">Yes</label>
                                        </p>
                                        <p className="custom-checkbox-wrap">
                                            <input
                                                className="custom-checkbox"
                                                id="include-archived-no"
                                                name="includeArchived"
                                                type="radio"
                                                value="false"
                                                onChange={this.handleBooleanInputChanged}
                                                checked={!this.state.includeArchived}
                                            />
                                            <label htmlFor="include-archived-no">No</label>
                                        </p>
                                    </div>
                                </div>
                                <div className="form-actions">
                                    <div className="button-actions text-center">

                                        {this.state.isSubmitting &&
                                            this.state.submitType === "excel" ? (
                                            <button
                                                className="btn btn-primary"
                                                onClick={this.handleViewExcel}
                                                disabled
                                            >
                                                <i className="icon-spinner icon-pulse"></i> Generating
                                            </button>
                                        ) : (
                                            <button
                                                className="btn btn-primary"
                                                onClick={this.handleViewExcel}
                                                disabled={this.state.isSubmitting}
                                            >
                                                View
                                            </button>
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private handleInputChanged(
        event: React.SyntheticEvent<HTMLInputElement | HTMLSelectElement>
    ) {
        this.setState({
            [event.currentTarget.name]: event.currentTarget.value,
        } as any);
    }

    private handleBooleanInputChanged(
        event: React.SyntheticEvent<HTMLInputElement>
    ) {
        this.setState({
            [event.currentTarget.name]: event.currentTarget.value === "true",
        } as any);
    }

    private handleFromDateChanged(date: moment.Moment) {
        this.setState({
            fromDate: date,
        });
    }

    private handleToDateChanged(date: moment.Moment) {
        this.setState({
            toDate: date,
        });
    }
    private handleCustomerSelected(value: Option | null) {
        this.setState({
            customerId: value !== null ? (value!.value as number) : undefined,
        });
    }

    private handleJobSelected(value: Option | null) {
        this.setState({
            jobId: value !== null ? (value!.value as number) : undefined,
        });
    }

    private handleProductSelected(value: Option | null) {
        this.setState({
            productId: value !== null ? (value!.value as number) : undefined,
        });
    }

    private async handleViewExcel(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.setState({
            isSubmitting: true,
            submitType: "excel",
        });

        let data = {
            fromDate: this.state.fromDate,
            toDate: this.state.toDate,
            quoteStatusId: this.state.quoteStatusId,
            salespersonUserId: this.state.salespersonUserId,
            customerId: this.state.customerId,
            jobId: this.state.jobId,
            city: this.state.city,
            productId: this.state.productId,
            includeArchived: this.state.includeArchived,
        };

        const response = await apifetch(
            "reports/quotes.xlsx",
            {
                headers: new Headers({
                    Accept: "application/json",
                    "Content-Type": "application/json",
                }),
                method: "POST",
                body: JSON.stringify(data),
            },
            true
        );
        await downloadResponse(response);

        this.setState({
            isSubmitting: false,
            submitType: undefined,
        });
    }
}



// WEBPACK FOOTER //
// ./ClientApp/components/reports/Quotes.tsx